import { AnimatePresence, motion } from "framer-motion";
import React, { useContext, useState } from "react";
import { ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { FaSpinner } from "react-icons/fa";
import { HiOutlineEmojiSad } from "react-icons/hi";
import { LuActivity, LuBriefcase, LuBuilding, LuBuilding2, LuCalendar, LuCircleAlert, LuContact, LuGlobe, LuKey, LuLocate, LuMail, LuMapPin, LuPhone, LuShield, LuSmartphone, LuUser, LuUserCheck, LuUsers } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { AuditItem, AuditObjectTypes, RoleResponseModel } from "../../_core/_model.ts";
import { GerUsersRoleGroup, GetObjectAudits, GetUserRoles } from "../../_core/_request.ts";
import { AccessTypes, LabelConstants } from "../../Constants.ts";
import { useAppContext } from "../../context/AppContext.tsx";
import { AuthContext } from "../../context/AuthContext.js";
import { getMyLocalTime, getUserName } from "../../Utilities/Utilities.ts";
import AuditCard from "../Audit/AuditCard.tsx";
import ComponentLayout from "../Layouts/ComponentLayout.tsx";
import { RoleGroup } from "../Roles/_core/_model.ts";

const profileTabs = [
    { key: "Personal Info", name: "Personal Info", icon: <LuUser size={18} /> },
    { key: "Security", name: "Security", icon: <LuShield size={18} /> },
    { key: "Activity", name: "Activity", icon: <LuActivity size={18} /> }
];

// Helper component for clean Personal Info Grid
// Removed all forced background colors. Relies on transparent inheritance with borders.
const InfoCard = ({ icon, label, value }: { icon: React.ReactNode, label: string, value: string | React.ReactNode }) => (
    <div className="col-md-6 col-lg-4">
        <div className="d-flex align-items-start gap-3 p-3 rounded-4 border shadow-sm h-100 transition-all bg-theme-card">
            <div className="d-flex align-items-center justify-content-center text-success flex-shrink-0" style={{ width: '40px', height: '40px', backgroundColor: 'rgba(25, 135, 84, 0.1)', borderRadius: '10px' }}>
                {icon}
            </div>
            <div className="d-flex flex-column min-w-0">
                <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>{label}</small>
                <span className="fw-medium mt-1 text-truncate" title={typeof value === 'string' ? value : ''}>{value}</span>
            </div>
        </div>
    </div>
);

export function UserProfile() {
    const navigate = useNavigate();
    const [audits, setAudits] = useState<AuditItem[] | null>(null);
    const { user } = useContext(AuthContext);

    const myUser = user.userFirstName != "" ? user.userFirstName : user.userName;
    const myFullUser = user.userFirstName != "" ? user.userFirstName + " " + user.userLastName : user.userName;

    const { allUsers, roles, allRoleGroups } = useAppContext();
    const myManager = ((user.managerUserID == null || user.managerUserID == 0)) ? null : getUserName(allUsers?.find((x) => x.userID == user.managerUserID));

    const [activeTab, setActiveTab] = useState<string>("Personal Info");
    const [myRoles, setMyRoles] = useState<RoleResponseModel[] | null>(null);
    const [myRoleGroups, setMyRoleGroups] = useState<RoleGroup[] | null>(null);

    const getMyRoles = () => {
        GetUserRoles(user.userID).then((x) => {
            let mRoles = x.data.map((r) => r.roleID);
            let roleList = roles.filter((r) => r.roleID != null && mRoles.indexOf(r.roleID) > -1)
            setMyRoles([...roleList]);
        });
        GerUsersRoleGroup(user.userID).then((x) => {
            setMyRoleGroups([...x.data]);
        });
    }

    const updateTab = (t: string) => {
        if (t == "Security") {
            getMyRoles();
        }
        else if (t == "Activity") {
            getMyAudit();
        }
        setActiveTab(t);
    }

    const getMyAudit = () => {
        GetObjectAudits(AuditObjectTypes.User, user.userID, 1000)
            .then((x) => setAudits([...x.data]))
            .catch(() => setAudits([]));
    }

    const getName = (item: AuditItem) => {
        return item.forInstanceName || "Unnamed";
    };

    const loading = (msg: string) => {
        return <div className="d-flex flex-column align-items-center text-success mt-5">
            <AnimatePresence>
                <motion.div
                    animate={{ rotate: 360 }}
                    style={{ display: 'inline-block', width: '40px', height: '40px', marginBottom: '15px' }}
                    transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                >
                    <FaSpinner className="fa-spin mb-2" size={32} style={{ color: '#198754', width: '100%', height: '100%' }} />
                </motion.div>
            </AnimatePresence>
            <span className="fw-medium text-muted">{msg}</span>
        </div>
    }

    return (
        <ComponentLayout
            title={
                <div className="d-flex flex-row align-items-center">
                    <div className="rounded-circle bg-success text-white d-flex justify-content-center align-items-center shadow-sm" style={{ width: "64px", height: "64px", fontSize: "28px", fontWeight: "600" }}>
                        {myUser.charAt(0).toUpperCase()}
                    </div>
                    <div className="ms-3 d-flex flex-column gap-1">
                        <h2 className="h4 mb-0 p-0 fw-bold">{myFullUser}</h2>
                        <small className="text-muted fw-normal fs-7 m-0">
                            {AccessTypes[user.applicationAccess]?.value || 'User'}
                        </small>
                    </div>
                </div>
            }
            fixedLayout={true}
            instanceId={undefined}
            bodyHeight={0}
            exButtons={
                <button
                    onClick={() => navigate("/profile/edit?id=" + user.userID + "&back=profile")}
                    className="btn btn-outline-secondary ms-auto rounded-pill px-4 fw-semibold"
                >
                    Edit Profile
                </button>
            }
            objectType={undefined}
            subHeader={
                <ToggleButtonGroup
                    type="radio"
                    name="tabOptions"
                    value={activeTab}
                    onChange={updateTab}
                    className="mb-2"
                >
                    {profileTabs.map((tab) => (
                        <ToggleButton
                            key={tab.key}
                            id={`tabs-${tab.name}`}
                            variant={(activeTab == tab.name) ? 'outline-success active' : 'outline-success'}
                            value={tab.name}
                        >
                            <span className='d-flex flex-row align-items-center gap-2 py-1'>
                                {tab.icon}
                                {tab.name}
                            </span>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            }
            cBody={
                <div className="w-100 px-4 pt-3 overflow-auto" style={{ minHeight: '100%' }}>
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="pb-5 py-1 px-0"
                    >
                        {
                            activeTab == "Personal Info" ? (
                                <div className="row g-4 mx-0">
                                    {/* Core Info */}
                                    <InfoCard icon={<LuUser size={20} />} label="Display Name" value={myFullUser || "-"} />
                                    <InfoCard icon={<LuMail size={20} />} label="Email Address" value={user.email || "-"} />
                                    <InfoCard icon={<LuShield size={20} />} label="Account Status" value={user.status || "-"} />
                                    <InfoCard icon={<LuCircleAlert size={20} />} label="Account Enabled" value={user.accountEnabled !== undefined ? (user.accountEnabled ? "Yes" : "No") : "-"} />

                                    {/* Job & Department Details */}
                                    <InfoCard icon={<LuBriefcase size={20} />} label="Job Title" value={user.jobTitle || "-"} />
                                    <InfoCard icon={<LuBuilding size={20} />} label="Department" value={user.department || "-"} />
                                    <InfoCard icon={<LuBuilding2 size={20} />} label="Company Name" value={user.companyName || "-"} />
                                    <InfoCard icon={<LuContact size={20} />} label="Employee Type" value={user.employeeType || "-"} />
                                    <InfoCard icon={<LuUsers size={20} />} label="Manager" value={user.managerName || myManager || "-"} />

                                    {/* Location Details */}
                                    <InfoCard icon={<LuMapPin size={20} />} label="City" value={user.city || "-"} />
                                    <InfoCard icon={<LuGlobe size={20} />} label="Country" value={user.country || "-"} />
                                    <InfoCard icon={<LuLocate size={20} />} label="Office Location" value={user.officeLocation || "-"} />

                                    {/* Contact & System Details */}
                                    <InfoCard icon={<LuSmartphone size={20} />} label="Mobile Phone" value={user.mobilePhone || "-"} />
                                    <InfoCard icon={<LuPhone size={20} />} label="Business Phones" value={user.businessPhones || "-"} />
                                    {/* <InfoCard icon={<LuUserCheck size={20} />} label="Approver Status" value={user.isApprover == 1 ? "Yes" : "No"} /> */}
                                    <InfoCard icon={<LuCalendar size={20} />} label="Created At" value={getMyLocalTime(user.createdAt) || "-"} />
                                </div>
                            ) :
                                activeTab == "Security" ? (
                                    ((myRoles == null || myRoleGroups == null) ? loading("Loading Security Data...") :
                                        <div className="d-flex flex-column gap-4 max-w-100">

                                            {/* Password Management Section (No .card class) */}
                                            {/* <div className="border shadow-sm rounded-3 overflow-hidden p-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="bg-success bg-opacity-10 p-3 rounded-circle text-success">
                                                        <LuKey size={24} />
                                                    </div>
                                                    <div>
                                                        <h5 className="mb-1 fw-bold">Password & Authentication</h5>
                                                        <p className="text-muted-theme mb-0 small">Update your password regularly to keep your account secure.</p>
                                                    </div>
                                                </div>
                                                <button
                                                    className="btn btn-outline-success px-4 py-2 rounded-3 fw-semibold text-nowrap"
                                                    onClick={() => navigate('/change-password')}
                                                >
                                                    Change Password
                                                </button>
                                            </div> */}

                                            {/* Roles and Permissions Section (No .card class) */}
                                            <div className="border shadow-sm rounded-3 overflow-hidden p-4">
                                                <div className="d-flex align-items-center gap-2 mb-4 pb-3 border-bottom">
                                                    <LuShield className="text-success fs-4" />
                                                    <h5 className="mb-0 fw-bold">Access & Permissions</h5>
                                                </div>

                                                <div className="mb-4">
                                                    <h6 className="text-muted-theme fw-semibold mb-3 text-uppercase" style={{ fontSize: '0.8rem', letterSpacing: '0.5px' }}>
                                                        {LabelConstants.RoleGroups}
                                                    </h6>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {myRoleGroups.length > 0 ? (
                                                            myRoleGroups.map((r) => (
                                                                <span key={r.roleGroupID} className="border px-3 py-2 rounded-3 fw-medium fs-7 shadow-sm">
                                                                    {allRoleGroups.find(rg => rg.roleGroupID === r.roleGroupID)?.roleGroupName}
                                                                </span>
                                                            ))
                                                        ) : (
                                                            <span className="text-muted-theme bg-gray py-1 px-3 rounded-3 fs-7 d-flex align-items-center gap-1">
                                                                <HiOutlineEmojiSad size={16} /> No Groups Assigned
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div>
                                                    <h6 className="text-muted-theme fw-semibold mb-3 text-uppercase" style={{ fontSize: '0.8rem', letterSpacing: '0.5px' }}>
                                                        Assigned Roles
                                                    </h6>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {myRoles.length > 0 ? (
                                                            myRoles.map((r) => (
                                                                <span key={r.roleID} className="border px-3 py-2 rounded-3 fw-medium fs-7 shadow-sm">
                                                                    {r.roleName}
                                                                </span>
                                                            ))
                                                        ) : (
                                                            <span className="text-muted-theme bg-gray py-1 px-3 rounded-3 fs-7 d-flex align-items-center gap-1">
                                                                <HiOutlineEmojiSad size={16} /> No Roles Assigned
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>)
                                ) :
                                    (
                                        audits == null ? loading("Loading Activity Logs...") :
                                            (audits.length > 0) ? (
                                                <div className="border shadow-sm rounded-3 p-3">
                                                    <ul className="list-unstyled px-0 mx-0 mb-0 d-flex flex-column gap-3">
                                                        {audits?.map((audit, idx) => (
                                                            <li key={idx}>
                                                                <AuditCard audit={audit} name={getName(audit)} />
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <div className="border rounded-circle d-inline-flex p-4 mb-3 text-muted-theme">
                                                        <LuActivity size={48} opacity={0.5} />
                                                    </div>
                                                    <h5 className="fw-bold">No Activity Yet</h5>
                                                    <p className="text-muted-theme">There are no audit logs recorded for your account.</p>
                                                </div>
                                            )
                                    )
                        }
                    </motion.div>
                </div>
            }
        />
    )
}

export default UserProfile;